import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { CopyIdButton, TableActionMenu, type TableActionItem } from '@/components/shared';
import {
  formatDateTime,
  formatShortId,
  formatUserDisplayName,
  maskEmailAddress,
} from '@/views/users/format';
import type { BackofficeOrganizationMemberRow, OrganizationMembersSortBy } from '../types';

const SORTABLE_HEADERS: Record<OrganizationMembersSortBy, string> = {
  name: 'Name',
  lastSeenOnlineAt: 'Last seen online',
  addedAt: 'Added',
};

export interface OrganizationMembersTableProps {
  members: BackofficeOrganizationMemberRow[];
  sortBy: OrganizationMembersSortBy;
  sortDirection: 'asc' | 'desc';
  getActions: (member: BackofficeOrganizationMemberRow) => TableActionItem[];
  onSortChange: (sortBy: OrganizationMembersSortBy) => void;
}

function SortButton({
  label,
  field,
  sortBy,
  sortDirection,
  onSortChange,
}: {
  label: string;
  field: OrganizationMembersSortBy;
  sortBy: OrganizationMembersSortBy;
  sortDirection: 'asc' | 'desc';
  onSortChange: (sortBy: OrganizationMembersSortBy) => void;
}) {
  const active = field === sortBy;
  const Icon = active ? (sortDirection === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;

  return (
    <button
      type="button"
      className="focus-ring inline-flex items-center gap-1 rounded-md text-left font-semibold text-neutral-600"
      aria-label={`Sort by ${label}`}
      onClick={() => onSortChange(field)}
    >
      <span>{label}</span>
      <Icon className="h-3.5 w-3.5" aria-hidden="true" />
    </button>
  );
}

export function OrganizationMembersTable({
  members,
  sortBy,
  sortDirection,
  getActions,
  onSortChange,
}: OrganizationMembersTableProps) {
  const navigate = useNavigate();

  return (
    <div className="overflow-x-auto">
      <table className="min-w-[980px] w-full border-collapse text-left">
        <thead className="border-b border-neutral-200 bg-white text-small text-neutral-500">
          <tr>
            <th scope="col" className="min-w-[150px] px-4 py-3 font-semibold text-neutral-600">
              ID
            </th>
            <th scope="col" className="min-w-[280px] px-4 py-3">
              <SortButton
                label={SORTABLE_HEADERS.name}
                field="name"
                sortBy={sortBy}
                sortDirection={sortDirection}
                onSortChange={onSortChange}
              />
            </th>
            <th scope="col" className="min-w-[180px] px-4 py-3 font-semibold text-neutral-600">
              Role
            </th>
            <th scope="col" className="min-w-[190px] px-4 py-3">
              <SortButton
                label={SORTABLE_HEADERS.lastSeenOnlineAt}
                field="lastSeenOnlineAt"
                sortBy={sortBy}
                sortDirection={sortDirection}
                onSortChange={onSortChange}
              />
            </th>
            <th scope="col" className="min-w-[190px] px-4 py-3">
              <SortButton
                label={SORTABLE_HEADERS.addedAt}
                field="addedAt"
                sortBy={sortBy}
                sortDirection={sortDirection}
                onSortChange={onSortChange}
              />
            </th>
            <th scope="col" className="w-14 px-4 py-3">
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-neutral-100 text-body">
          {members.map((member) => {
            const displayName = formatUserDisplayName({
              name: member.name,
              email: member.email,
            });
            const isPendingInvite = member.kind === 'invitation';
            const detailPath = member.subjectUserId
              ? `/users/${encodeURIComponent(member.subjectUserId)}`
              : null;
            const actions = getActions(member);

            return (
              <tr
                key={member.id}
                className={detailPath ? 'cursor-pointer hover:bg-backoffice-50' : undefined}
                aria-label={detailPath ? `Open ${displayName}` : undefined}
                onClick={
                  detailPath
                    ? () => {
                        navigate(detailPath);
                      }
                    : undefined
                }
                onKeyDown={
                  detailPath
                    ? (event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          navigate(detailPath);
                        }
                      }
                    : undefined
                }
                tabIndex={detailPath ? 0 : undefined}
              >
                <td className="px-4 py-4 text-neutral-700">
                  <div className="group inline-flex items-center gap-2">
                    <span className="font-medium tabular-nums text-neutral-700">
                      {formatShortId(member.id)}
                    </span>
                    <CopyIdButton
                      value={member.id}
                      label={`Copy ${isPendingInvite ? 'invitation' : 'member'} ID`}
                    />
                  </div>
                </td>
                <td className="px-4 py-4">
                  <div className="min-w-0">
                    <div className="group inline-flex items-center gap-2">
                      <span className="font-medium text-neutral-900">{displayName}</span>
                      <CopyIdButton value={displayName} label="Copy name" />
                    </div>
                    <div className="mt-1 group inline-flex items-center gap-2 text-small text-neutral-600">
                      <span>{maskEmailAddress(member.email)}</span>
                      <CopyIdButton value={member.email} label="Copy email" />
                    </div>
                  </div>
                </td>
                <td className="px-4 py-4 text-neutral-700">
                  {isPendingInvite ? 'Pending invite' : member.roleLabel}
                </td>
                <td className="px-4 py-4 text-neutral-700">
                  {formatDateTime(member.lastSeenOnlineAt)}
                </td>
                <td className="px-4 py-4 text-neutral-700">{formatDateTime(member.addedAt)}</td>
                <td className="px-4 py-4">
                  {actions.length > 0 ? (
                    <div
                      onClick={(event) => event.stopPropagation()}
                      onKeyDown={(event) => event.stopPropagation()}
                    >
                      <TableActionMenu
                        portal
                        actions={actions}
                        label={`Actions for ${displayName}`}
                        containerClassName="w-auto"
                      />
                    </div>
                  ) : null}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
